// Spec: docs/plans/frontend-stack.md §3.3 (per-skin contract — admin uses
// the modern stack but is not a "skin" itself; it's a separate Pages
// project for staff per CLAUDE.md MVA scope).

import { useState } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import {
  Outlet,
  RouterProvider,
  createRootRoute,
  createRoute,
  createRouter,
  redirect,
} from '@tanstack/react-router';
import { AuthGuard } from '@/lib/auth';
import { Shell } from '@/components/layout/shell';
import { LoginPage } from '@/pages/login';
import { DashboardPage } from '@/pages/dashboard';
import { UsersPage } from '@/pages/users';
import { UserProfilePage } from '@/pages/user-profile';
import { AuditPage } from '@/pages/audit';
import { IpBansPage } from '@/pages/ip-bans';
import { SupportInboxPage } from '@/pages/support-inbox';
import { CronHealthPage } from '@/pages/cron-health';
import { CasinoMetricsPage } from '@/pages/casino-metrics';
// Economy-observability dashboards (epic #550, Phase 1 — #559/#561–#566).
import { BankingMetricsPage } from '@/pages/banking-metrics';
import { CrimeMetricsPage } from '@/pages/crime-metrics';
import { GymMetricsPage } from '@/pages/gym-metrics';
import { CareerMetricsPage } from '@/pages/career-metrics';
import { MarketMetricsPage } from '@/pages/market-metrics';
import { PlayerEconomyMetricsPage } from '@/pages/player-economy-metrics';
import { XpMetricsPage } from '@/pages/xp-metrics';
import { EconomySummaryPage } from '@/pages/economy-summary';
import { ConfigPage } from '@/pages/config';
import { NewspaperPage } from '@/pages/newspaper';
import { PaymentsPage } from '@/pages/payments';
import { UserTransactionsPage } from '@/pages/user-transactions';
import { PaymentDetailPage } from '@/pages/payment-detail';
import { StaffPage } from '@/pages/staff';
import { WebauthnSettingsPage } from '@/pages/webauthn-settings';

// ── Route definitions ─────────────────────────────────────────────────────────

const rootRoute = createRootRoute({
  component: () => (
    <div className="min-h-dvh">
      <Outlet />
    </div>
  ),
});

// / → redirect to /login by default (AuthGuard will redirect to /dashboard if authed)
const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  beforeLoad: () => {
    throw redirect({ to: '/login' });
  },
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  component: LoginPage,
});

const dashboardRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/dashboard',
  component: () => (
    <AuthGuard>
      <Shell>
        <DashboardPage />
      </Shell>
    </AuthGuard>
  ),
});

const usersRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/users',
  component: () => (
    <AuthGuard>
      <Shell>
        <UsersPage />
      </Shell>
    </AuthGuard>
  ),
});

const userProfileRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/users/$user_id',
  component: () => (
    <AuthGuard>
      <Shell>
        <UserProfilePage />
      </Shell>
    </AuthGuard>
  ),
});

const auditRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/audit',
  component: () => (
    <AuthGuard>
      <Shell>
        <AuditPage />
      </Shell>
    </AuthGuard>
  ),
});

const ipBansRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/ip-bans',
  component: () => (
    <AuthGuard>
      <Shell>
        <IpBansPage />
      </Shell>
    </AuthGuard>
  ),
});

const supportInboxRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/support-inbox',
  component: () => (
    <AuthGuard>
      <Shell>
        <SupportInboxPage />
      </Shell>
    </AuthGuard>
  ),
});

const cronHealthRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/cron-health',
  component: () => (
    <AuthGuard>
      <Shell>
        <CronHealthPage />
      </Shell>
    </AuthGuard>
  ),
});

const casinoMetricsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/casino-metrics',
  component: () => (
    <AuthGuard>
      <Shell>
        <CasinoMetricsPage />
      </Shell>
    </AuthGuard>
  ),
});

// ── Economy-observability dashboards (epic #550, Phase 1 — #559/#561–#566) ──────

const bankingMetricsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/banking-metrics',
  component: () => (
    <AuthGuard>
      <Shell>
        <BankingMetricsPage />
      </Shell>
    </AuthGuard>
  ),
});

const crimeMetricsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/crime-metrics',
  component: () => (
    <AuthGuard>
      <Shell>
        <CrimeMetricsPage />
      </Shell>
    </AuthGuard>
  ),
});

const gymMetricsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/gym-metrics',
  component: () => (
    <AuthGuard>
      <Shell>
        <GymMetricsPage />
      </Shell>
    </AuthGuard>
  ),
});

const careerMetricsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/career-metrics',
  component: () => (
    <AuthGuard>
      <Shell>
        <CareerMetricsPage />
      </Shell>
    </AuthGuard>
  ),
});

const marketMetricsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/market-metrics',
  component: () => (
    <AuthGuard>
      <Shell>
        <MarketMetricsPage />
      </Shell>
    </AuthGuard>
  ),
});

const playerEconomyMetricsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/player-economy-metrics',
  component: () => (
    <AuthGuard>
      <Shell>
        <PlayerEconomyMetricsPage />
      </Shell>
    </AuthGuard>
  ),
});

const xpMetricsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/xp-metrics',
  component: () => (
    <AuthGuard>
      <Shell>
        <XpMetricsPage />
      </Shell>
    </AuthGuard>
  ),
});

const economySummaryRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/economy-summary',
  component: () => (
    <AuthGuard>
      <Shell>
        <EconomySummaryPage />
      </Shell>
    </AuthGuard>
  ),
});

const configRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/config',
  component: () => (
    <AuthGuard>
      <Shell>
        <ConfigPage />
      </Shell>
    </AuthGuard>
  ),
});

const newspaperRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/newspaper',
  component: () => (
    <AuthGuard>
      <Shell>
        <NewspaperPage />
      </Shell>
    </AuthGuard>
  ),
});

const paymentsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/payments',
  component: () => (
    <AuthGuard>
      <Shell>
        <PaymentsPage />
      </Shell>
    </AuthGuard>
  ),
});

const paymentDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/payments/$txn_id',
  component: () => (
    <AuthGuard>
      <Shell>
        <PaymentDetailPage />
      </Shell>
    </AuthGuard>
  ),
});

const staffRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/staff',
  component: () => (
    <AuthGuard>
      <Shell>
        <StaffPage />
      </Shell>
    </AuthGuard>
  ),
});

const webauthnSettingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/settings/webauthn',
  component: () => (
    <AuthGuard>
      <Shell>
        <WebauthnSettingsPage />
      </Shell>
    </AuthGuard>
  ),
});

const userTransactionsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/users/$user_id/transactions',
  component: () => (
    <AuthGuard>
      <Shell>
        <UserTransactionsPage />
      </Shell>
    </AuthGuard>
  ),
});

// ── Router ────────────────────────────────────────────────────────────────────

const routeTree = rootRoute.addChildren([
  indexRoute,
  loginRoute,
  dashboardRoute,
  usersRoute,
  userProfileRoute,
  auditRoute,
  ipBansRoute,
  supportInboxRoute,
  cronHealthRoute,
  casinoMetricsRoute,
  bankingMetricsRoute,
  crimeMetricsRoute,
  gymMetricsRoute,
  careerMetricsRoute,
  marketMetricsRoute,
  playerEconomyMetricsRoute,
  xpMetricsRoute,
  economySummaryRoute,
  configRoute,
  newspaperRoute,
  paymentsRoute,
  paymentDetailRoute,
  userTransactionsRoute,
  staffRoute,
  webauthnSettingsRoute,
]);

const router = createRouter({ routeTree });

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

// ── App ───────────────────────────────────────────────────────────────────────

export default function AdminApp() {
  const [queryClient] = useState(() => new QueryClient());
  return (
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
    </QueryClientProvider>
  );
}
